// src/utils/importHelpers.js
// Fonctions pures de l'import CSV/Excel (ImportCSVPremium) : lecture des valeurs, rapprochement des colonnes,
// contrôle avant import et rapport. Aucune dépendance à React ni à la base : testable seul.

// ─── Lecture des valeurs ──────────────────────────────────────────────────────
import { makeT } from "../locales/importCopy";

export const fold = (s) =>
  String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

export const HERITAGE_LABELS = {
  gallery_stock: "Stock marchand de la galerie",
  personal: "Propriété personnelle",
  company: "Propriété de la société",
  deposit: "Dépôt",
  consignment: "Consignation",
  entrusted: "Œuvre confiée",
  to_clarify: "Statut à clarifier",
};

// Libellés anglais courants (Artlogic, tableurs) → clés internes.
const HERITAGE_EXTRA = {
  gallery_stock: ["stock", "stock marchand", "gallery stock", "gallery commercial stock", "stock galerie", "stock de la galerie", "in stock"],
  personal: ["personal", "personal property", "private", "collection personnelle", "propriete personnelle", "propriete du galeriste"],
  company: ["company", "company property", "societe", "propriete societe", "propriete de la societe"],
  deposit: ["deposit", "depot", "en depot", "on deposit", "loan", "pret"],
  consignment: ["consignment", "consignation", "en consignation", "on consignment", "commission"],
  entrusted: ["entrusted", "confie", "oeuvre confiee", "confiee"],
  to_clarify: ["to clarify", "a clarifier", "unknown", "inconnu", "to verify", "a verifier"],
};

// Accepte la clé (personal), le libellé français (« Propriété personnelle ») ou un équivalent courant ; sinon null.
export function normalizeHeritageStatus(raw) {
  if (raw == null || raw === "") return null;
  const f = fold(raw);
  for (const [key, label] of Object.entries(HERITAGE_LABELS)) {
    if (f === fold(key) || f === fold(label)) return key;
    if ((HERITAGE_EXTRA[key] || []).some((x) => fold(x) === f)) return key;
  }
  return null;
}

export function parseDimensions(raw) {
  if (!raw) return { height: null, width: null, depth: null, unit: "cm" };
  const cleaned = String(raw).replace(/×/g, "x").replace(/X/g, "x").replace(/\s+/g, "").trim();
  const parts = cleaned.split("x").map((p) => parseFloat(p.replace(",", "."))).filter((n) => !isNaN(n));
  if (parts.length === 2) return { height: parts[0], width: parts[1], depth: null, unit: "cm" };
  if (parts.length === 3) return { height: parts[0], width: parts[1], depth: parts[2], unit: "cm" };
  return { height: null, width: null, depth: null, unit: "cm" };
}

export function cleanValue(val) {
  return (val === "" || val === null || val === undefined) ? null : val;
}

export function toNumber(val) {
  if (val === "" || val === null || val === undefined) return null;
  const cleaned = String(val).replace(/[€$£\s  ]/g, "").replace(",", ".");
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

const MONTHS = {
  janvier: 1, january: 1, jan: 1, janv: 1, fevrier: 2, february: 2, feb: 2, fev: 2, mars: 3, march: 3, mar: 3, avril: 4, april: 4, apr: 4, avr: 4,
  mai: 5, may: 5, juin: 6, june: 6, jun: 6, juillet: 7, july: 7, jul: 7, juil: 7, aout: 8, august: 8, aug: 8,
  septembre: 9, september: 9, sep: 9, sept: 9, octobre: 10, october: 10, oct: 10, novembre: 11, november: 11, nov: 11, decembre: 12, december: 12, dec: 12,
};
// Jour/mois/année plausibles → « AAAA-MM-JJ », sinon null.
function ymd(y, m, d) {
  const Y = Number(y), M = Number(m), D = Number(d);
  if (!(Y >= 1000 && Y < 3000 && M >= 1 && M <= 12 && D >= 1 && D <= 31)) return null;
  return `${String(Y)}-${String(M).padStart(2, "0")}-${String(D).padStart(2, "0")}`;
}

export function excelDateToISO(excelDate) {
  if (!excelDate) return null;
  const str = String(excelDate).trim();
  if (str.match(/^\d{4}-\d{2}-\d{2}$/)) return str;
  if (str.match(/^\d{4}$/)) return null; // année seule → pas une date
  const fr = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (fr) return `${fr[3]}-${fr[2].padStart(2, "0")}-${fr[1].padStart(2, "0")}`;
  // 12.03.2019 · 12-03-2019 (jour d'abord, usage français et allemand)
  const dots = str.match(/^(\d{1,2})[.-](\d{1,2})[.-](\d{4})$/);
  if (dots) return ymd(dots[3], dots[2], dots[1]);
  // 2019/03/12 · 2019.03.12 · 2019-03-12T10:00:00
  const isoLike = str.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})(?:[T\s].*)?$/);
  if (isoLike) return ymd(isoLike[1], isoLike[2], isoLike[3]);
  // 12 mars 2019 · March 12, 2019 · 12 Mar 2019
  const dmy = fold(str).match(/^(\d{1,2}) ([a-z]+) (\d{4})$/);
  if (dmy && MONTHS[dmy[2]]) return ymd(dmy[3], MONTHS[dmy[2]], dmy[1]);
  const mdy = fold(str).match(/^([a-z]+) (\d{1,2}) (\d{4})$/);
  if (mdy && MONTHS[mdy[1]]) return ymd(mdy[3], MONTHS[mdy[1]], mdy[2]);
  const days = typeof excelDate === "number" ? excelDate : parseFloat(str.replace(",", "."));
  if (!isNaN(days) && days > 30000 && days < 60000) {
    return new Date((days - 25569) * 86400 * 1000).toISOString().split("T")[0];
  }
  return null;
}

// ─── Devise et disponibilité, ligne par ligne ───────────────────────────────────────────────
export const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "JPY", "CNY"];
const CURRENCY_WORDS = {
  EUR: ["eur", "euro", "euros", "€"],
  USD: ["usd", "us$", "dollar", "dollars", "dollar us", "$"],
  GBP: ["gbp", "livre", "livres", "pound", "pounds", "sterling", "£"],
  CHF: ["chf", "franc suisse", "francs suisses", "swiss franc", "fr s"],
  JPY: ["jpy", "yen", "yens", "¥"],
  CNY: ["cny", "rmb", "yuan", "yuans", "renminbi"],
};
// « € », « euros », « eur » → « EUR » ; valeur inconnue → null.
export function normalizeCurrency(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const t = String(raw).trim();
  const f = fold(t);
  for (const [code, words] of Object.entries(CURRENCY_WORDS)) {
    if (t.toUpperCase() === code || words.some((w) => w === t.toLowerCase() || w === f)) return code;
  }
  return null;
}
// Devise écrite dans le montant lui-même (« 12 000 € », « $5,000 ») ; sinon null.
export function currencyFromAmount(raw) {
  if (raw == null) return null;
  const t = String(raw);
  if (/€/.test(t) || /\beur(os?)?\b/i.test(t)) return "EUR";
  if (/£/.test(t) || /\bgbp\b/i.test(t)) return "GBP";
  if (/\bchf\b/i.test(t)) return "CHF";
  if (/\$/.test(t) || /\busd\b/i.test(t)) return "USD";
  if (/¥/.test(t) || /\bjpy\b/i.test(t)) return "JPY";
  return null;
}

export const AVAILABILITIES = ["En collection", "En prêt", "En vente", "Vendu", "Perdu", "Volé"];
const AVAILABILITY_WORDS = {
  "En collection": ["en collection", "collection", "in collection", "in stock", "kept", "conserve", "conservee", "not for sale", "pas a vendre", "nfs"],
  "En prêt": ["en pret", "pret", "on loan", "loan", "loaned", "prete"],
  "En vente": ["en vente", "a vendre", "disponible", "available", "for sale", "on sale"],
  "Vendu": ["vendu", "vendue", "sold"],
  "Perdu": ["perdu", "perdue", "lost"],
  "Volé": ["vole", "volee", "stolen"],
};
// Renvoie le libellé standard si l'équivalent est connu ; sinon null (la valeur d'origine est alors conservée par l'appelant).
export function normalizeAvailability(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const f = fold(raw);
  for (const [label, words] of Object.entries(AVAILABILITY_WORDS)) {
    if (f === fold(label) || words.some((w) => fold(w) === f)) return label;
  }
  return null;
}

// ─── Rapprochement des colonnes ───────────────────────────────────────────────
// Noms de colonnes courants (français et anglais, Artlogic, Excel, Airtable) pour chaque champ ARTICHAUT.
// Comparaison sans accents ni majuscules ni ponctuation. Volontairement prudent : des noms ambigus
// (« statut », « id », « prix », « valeur », « stock ») ne sont pas rapprochés tout seuls.
export const COLUMN_SYNONYMS = {
  title: ["titre", "title", "titre de l oeuvre", "titre oeuvre", "nom de l oeuvre", "oeuvre", "artwork", "artwork title", "work title", "intitule", "designation"],
  artist: ["artiste", "artist", "artist name", "nom de l artiste", "nom artiste", "auteur", "createur"],
  year: ["annee", "year", "date de creation", "annee de creation", "creation date", "creation year", "date oeuvre", "date of work"],
  medium: ["technique", "medium", "support", "materiaux", "materials", "media", "matiere", "technique medium"],
  description: ["description", "descriptif"],
  dimensions: ["dimensions", "dimension", "taille", "size", "format", "dimensions cm", "hxl", "h x l", "hauteur x largeur", "hxlxp", "h x l x p"],
  tags: ["mots cles", "mots cle", "tags", "keywords", "etiquettes"],
  typeOfArt: ["type d oeuvre", "type oeuvre", "type of art", "type of work", "categorie", "category", "discipline"],
  estimated_value: ["valeur estimee", "estimation", "estimated value", "value estimate", "estimate", "valeur d estimation", "estimation valeur"],
  currency: ["devise", "currency", "monnaie"],
  value: ["valeur numerique", "numeric value"],
  sale_price: ["prix de vente", "sale price", "selling price", "retail price", "prix public", "prix vente"],
  inventory_number: ["numero d inventaire", "n d inventaire", "no d inventaire", "n inventaire", "numero inventaire", "inventaire", "inventory number", "inventory no", "inventory", "inventory id", "stock number", "stock no", "stock n", "reference", "ref", "reference interne", "code oeuvre"],
  provenance: ["provenance", "origine", "historique de propriete", "provenance history", "ownership history"],
  acquisition_date: ["date d acquisition", "date acquisition", "acquisition date", "date d achat", "purchase date", "date achat", "acquired on"],
  acquisition_price: ["prix d acquisition", "prix acquisition", "acquisition price", "prix d achat", "purchase price", "prix achat", "cout", "cost", "cout d acquisition"],
  acquisition_type: ["type d acquisition", "type acquisition", "acquisition type", "mode d acquisition"],
  acquisition_source: ["source d acquisition", "acquisition source", "acquis aupres de", "fournisseur", "vendeur", "acquired from"],
  last_estimation_date: ["date de derniere estimation", "derniere estimation", "last valuation date", "last estimate date", "date estimation"],
  availability: ["disponibilite", "availability", "statut de disponibilite", "availability status"],
  condition: ["etat", "etat de conservation", "condition", "state of conservation", "conservation"],
  conditionNotes: ["commentaires sur l etat", "commentaire etat", "condition notes", "condition report", "notes d etat", "remarques etat"],
  editionInfo: ["edition", "edition info", "informations d edition", "numero d edition", "edition number", "tirage", "numero de tirage", "edition size"],
  signature: ["signature", "signee", "signed", "signe"],
  signature_desc: ["details signature", "detail signature", "signature details", "signature desc", "description signature", "inscriptions"],
  location_name: ["lieu", "lieu de stockage", "emplacement", "location", "storage location", "localisation", "place", "storage", "stockage"],
  heritage_status: ["statut patrimonial", "heritage status", "statut de detention", "detention", "propriete", "ownership", "ownership status", "statut de propriete", "type de detention"],
  external_source: ["plateforme", "source externe", "external source", "outil d origine", "platform"],
  external_id: ["id externe", "identifiant externe", "external id", "external identifier", "id plateforme", "artlogic id", "id artlogic", "platform id"],
  source_url: ["url", "lien", "link", "source url", "url source", "adresse de la fiche"],
};

// Renvoie { mapping: {champ: colonne}, how: {champ: "exact" | "synonyme"} }.
// Une colonne n'est utilisée que pour un seul champ. Priorité à l'ordre des champs cibles.
export function suggestMapping(headers, targetFields) {
  const used = new Set();
  const mapping = {};
  const how = {};
  const folded = (headers || []).map((h) => ({ h, f: fold(h) }));
  // 1) noms identiques aux champs ARTICHAUT (comportement historique)
  for (const tf of targetFields) {
    const hit = (headers || []).find((h) => !used.has(h) && String(h).toLowerCase() === tf.toLowerCase());
    if (hit !== undefined) { mapping[tf] = hit; how[tf] = "exact"; used.add(hit); }
  }
  // 2) noms courants français / anglais
  for (const tf of targetFields) {
    if (mapping[tf]) continue;
    const names = (COLUMN_SYNONYMS[tf] || []).map(fold);
    if (!names.length) continue;
    const hit = folded.find((c) => !used.has(c.h) && c.f && names.includes(c.f));
    if (hit) { mapping[tf] = hit.h; how[tf] = "synonyme"; used.add(hit.h); }
  }
  return { mapping, how };
}

// ─── Contrôle avant import ────────────────────────────────────────────────────
const NUMERIC_FIELDS = [
  ["estimated_value", "valeur estimée"], ["value", "valeur"], ["sale_price", "prix de vente"], ["acquisition_price", "prix d'acquisition"],
];
const DATE_FIELDS = [["acquisition_date", "date d'acquisition"], ["last_estimation_date", "date de dernière estimation"]];

const cell = (row, mapping, field) => {
  const col = mapping[field];
  if (!col) return "";
  const v = row[col];
  return v === null || v === undefined ? "" : v;
};
const filled = (v) => String(v ?? "").trim() !== "";

// Analyse chaque ligne SANS rien écrire. Renvoie les compteurs, les anomalies par ligne et un aperçu normalisé.
// ctx : { mapping, artistMode, artistFirst, artistLast, existingRefs: Set, dupStrategy, t } — t : traducteur (français par défaut)
export function analyzeRows(rows, ctx) {
  const t = (ctx && ctx.t) || makeT("fr");
  const { mapping = {}, artistMode = "single", artistFirst = "", artistLast = "", existingRefs = new Set(), dupStrategy = "autonumber" } = ctx || {};
  const seen = {};
  const lines = [];
  const counts = { total: rows.length, toCreate: 0, alreadyInBase: 0, duplicateInFile: 0, withIssues: 0 };

  rows.forEach((row, i) => {
    const issues = [];
    const title = String(cell(row, mapping, "title")).trim();
    const artist = artistMode === "merge"
      ? [artistFirst ? String(row[artistFirst] ?? "").trim() : "", artistLast ? String(row[artistLast] ?? "").trim() : ""].filter(Boolean).join(" ")
      : String(cell(row, mapping, "artist")).trim();
    const ref = String(cell(row, mapping, "inventory_number")).trim();

    if (!title) issues.push({ level: "info", text: t("Titre absent : la fiche s'appellera « Sans titre ».") });

    NUMERIC_FIELDS.forEach(([f, label]) => {
      const v = cell(row, mapping, f);
      if (filled(v) && toNumber(v) === null) issues.push({ level: "warn", text: t("{label} illisible (« {v} ») : ignorée.", { label: t(label), v: String(v).slice(0, 30) }) });
    });
    DATE_FIELDS.forEach(([f, label]) => {
      const v = cell(row, mapping, f);
      if (filled(v) && excelDateToISO(v) === null) {
        const yearOnly = /^\d{4}$/.test(String(v).trim());
        issues.push({ level: "warn", text: yearOnly ? t("{label} : une année seule n'est pas une date, ignorée.", { label: t(label) }) : t("{label} illisible (« {v} ») : ignorée.", { label: t(label), v: String(v).slice(0, 30) }) });
      }
    });
    const dim = cell(row, mapping, "dimensions");
    if (filled(dim)) {
      const d = parseDimensions(String(dim).replace(/,([0-9])/g, ".$1"));
      if (d.height === null && d.width === null) issues.push({ level: "warn", text: t("Dimensions illisibles (« {v} ») : attendu « hauteur × largeur ».", { v: String(dim).slice(0, 30) }) });
    }
    const cur = cell(row, mapping, "currency");
    if (filled(cur) && normalizeCurrency(cur) === null) issues.push({ level: "warn", text: t("Devise inconnue (« {v} ») : ignorée.", { v: String(cur).slice(0, 20) }) });
    const avail = cell(row, mapping, "availability");
    if (filled(avail) && normalizeAvailability(avail) === null) issues.push({ level: "info", text: t("Disponibilité « {v} » non reconnue : conservée telle quelle.", { v: String(avail).slice(0, 20) }) });
    const her = cell(row, mapping, "heritage_status");
    if (filled(her) && normalizeHeritageStatus(her) === null) issues.push({ level: "warn", text: t("Statut patrimonial inconnu (« {v} ») : laissé vide.", { v: String(her).slice(0, 30) }) });

    let state = "new";
    if (ref) {
      seen[ref] = (seen[ref] || 0) + 1;
      if (seen[ref] > 1) {
        state = "dupfile";
        counts.duplicateInFile += 1;
        issues.push({ level: "warn", text: dupStrategy === "skip" ? t("Référence « {ref} » déjà vue plus haut dans le fichier : ligne ignorée.", { ref }) : t("Référence « {ref} » répétée dans le fichier : renumérotée automatiquement.", { ref }) });
      }
      if (existingRefs.has(ref) && state === "new") {
        state = "inbase";
        counts.alreadyInBase += 1;
        issues.push({ level: "warn", text: t("Référence « {ref} » déjà présente dans vos œuvres : ligne ignorée à l'import.", { ref }) });
      }
    }
    if (state === "new") counts.toCreate += 1;
    if (issues.some((x) => x.level === "warn")) counts.withIssues += 1;
    lines.push({ index: i, line: i + 1, title, artist, ref, state, issues });
  });

  return { counts, lines };
}

// Rapport CSV (une ligne par anomalie) pour le contrôle avant import et le compte rendu final.
export function reportToCsv(entries, t = makeT("fr")) {
  const esc = (v) => {
    const s = String(v ?? "");
    return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = ["Ligne", "Titre", "Référence", "Type", "Détail"].map((h) => t(h));
  const body = entries.map((e) => [e.line, e.title || "", e.ref || "", e.type || "", e.detail || ""].map(esc).join(";"));
  return "﻿" + [head.join(";"), ...body].join("\r\n");
}

export function entriesFromAnalysis(analysis, t = makeT("fr")) {
  const out = [];
  (analysis?.lines || []).forEach((l) => {
    l.issues.forEach((x) => out.push({ line: l.line, title: l.title, ref: l.ref, type: x.level === "warn" ? t("À vérifier") : t("Information"), detail: x.text }));
  });
  return out;
}
